import { useState } from 'react';
import { DateUtils } from '@cleanpro/common-ui';
import { useParams, Link } from 'react-router-dom';
import { useProperties } from '../hooks/useProperties';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import { NumberUtils } from '@cleanpro/common-ui';
import { 
  ArrowLeft, 
  MapPin, 
  Home, 
  Users, 
  Calendar, 
  Key, 
  Heart, 
  Edit, 
  Save, 
  X,
  Clock,
  Square,
  Hash,
  Building,
  ExternalLink
} from 'lucide-react';
import { Property } from '../types';
import { useJobs } from '../../jobs/hooks/useJobs';

// Helper function to get status badge color
const getJobStatusBadge = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'confirmed':
      return 'bg-yellow-100 text-yellow-800';
    case 'requested':
    case 'pending':
      return 'bg-purple-100 text-purple-800';
    case 'in-progress':
    case 'en-route':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PropertyDetail: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { properties, updateProperty } = useProperties();
  const { customers } = useCustomers();
  const { jobs } = useJobs();
  const permissions = useRoleAccess();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Omit<Property, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    customerId: '',
    propertyType: 'house',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    squareFootage: undefined,
    bedrooms: 0,
    bathrooms: 0,
    floors: 1,
    accessInstructions: '',
    specialInstructions: '',
    hasPets: false,
    petDetails: '',
    preferredCleaningDay: undefined,
    requestedTimeSlot: undefined,
    isActive: true
  });

  const property = properties.find(p => p.id === propertyId);
  const customer = property ? customers.find(c => c.id === property.customerId) : undefined;

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;

    try {
      await updateProperty(property.id, editForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update property:', error);
    }
  };

  const handleEditCancel = () => {
    if (property) {
      setEditForm({
        name: property.name,
        customerId: property.customerId,
        propertyType: property.propertyType,
        addressLine1: property.addressLine1,
        addressLine2: property.addressLine2 || '',
        city: property.city,
        state: property.state,
        postalCode: property.postalCode,
        squareFootage: property.squareFootage,
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        floors: property.floors || 1,
        accessInstructions: property.accessInstructions || '',
        specialInstructions: property.specialInstructions || '',
        hasPets: property.hasPets,
        petDetails: property.petDetails || '',
        preferredCleaningDay: property.preferredCleaningDay,
        requestedTimeSlot: property.requestedTimeSlot,
        isActive: property.isActive
      });
    }
    setIsEditing(false);
  };

  const getPropertyTypeIcon = (type: string) => {
    switch (type) {
      case 'house': return <Home className="h-6 w-6" />;
      case 'apartment': return <Home className="h-6 w-6" />;
      case 'condo': return <Home className="h-6 w-6" />;
      case 'townhouse': return <Home className="h-6 w-6" />;
      case 'office': return <Users className="h-6 w-6" />;
      default: return <Home className="h-6 w-6" />;
    }
  };

  const formatAddress = (property: Property) => {
    return `${property.addressLine1}${property.addressLine2 ? ', ' + property.addressLine2 : ''}, ${property.city}, ${property.state} ${property.postalCode}`;
  };

  if (!property) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/properties" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Property Not Found</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">The property you're looking for doesn't exist or may have been deleted.</p>
          <Link to="/properties" className="mt-4 inline-block text-indigo-600 hover:text-indigo-700">
            ← Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/properties" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{property.addressLine1}</h1>
            <p className="text-gray-600 capitalize">{property.propertyType}</p>
          </div>
        </div>
        {permissions.canEditCustomers && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <Edit className="h-4 w-4" />
            <span>Edit Property</span>
          </button>
        )}
      </div>

      {isEditing ? (
        /* Edit Form */
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleEditSubmit} className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit Property</h2>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </div>

            <div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                <select
                  required
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={editForm.customerId}
                  onChange={(e) => setEditForm({...editForm, customerId: e.target.value})}
                >
                  <option value="">Select a customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={editForm.propertyType}
                onChange={(e) => setEditForm({...editForm, propertyType: e.target.value as Property['propertyType']})}
              >
                <option value="house">House</option>
                <option value="apartment">Apartment</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
                <option value="office">Office</option>
              </select>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Address</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
                <input
                  type="text"
                  required
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={editForm.addressLine1}
                  onChange={(e) => setEditForm({...editForm, addressLine1: e.target.value, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2 (Optional)</label>
                <input
                  type="text"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={editForm.addressLine2}
                  onChange={(e) => setEditForm({...editForm, addressLine2: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    required
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    required
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.state}
                    onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                  <input
                    type="text"
                    required
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.postalCode}
                    onChange={(e) => setEditForm({...editForm, postalCode: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Property Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Footage</label>
                  <input
                    type="number"
                    min="0"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.squareFootage || ''}
                    onChange={(e) => setEditForm({...editForm, squareFootage: e.target.value ? parseInt(e.target.value) : undefined})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
                  <input
                    type="number"
                    min="0"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.bedrooms || 0}
                    onChange={(e) => setEditForm({...editForm, bedrooms: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.bathrooms || 0}
                    onChange={(e) => setEditForm({...editForm, bathrooms: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Floors</label>
                  <input
                    type="number"
                    min="1"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.floors || 1}
                    onChange={(e) => setEditForm({...editForm, floors: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Cleaning Information</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access Instructions</label>
                <textarea
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows={3}
                  value={editForm.accessInstructions}
                  onChange={(e) => setEditForm({...editForm, accessInstructions: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Instructions</label>
                <textarea
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows={3}
                  value={editForm.specialInstructions}
                  onChange={(e) => setEditForm({...editForm, specialInstructions: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Pet Information</h3>
              <div className="flex items-center">
                <input
                  id="edit-has-pets"
                  type="checkbox"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  checked={editForm.hasPets}
                  onChange={(e) => setEditForm({...editForm, hasPets: e.target.checked})}
                />
                <label htmlFor="edit-has-pets" className="ml-2 block text-sm text-gray-900">
                  Has Pets
                </label>
              </div>
              {editForm.hasPets && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pet Details</label>
                  <textarea
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    rows={2}
                    value={editForm.petDetails}
                    onChange={(e) => setEditForm({...editForm, petDetails: e.target.value})}
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-900">Service Preferences</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Cleaning Day</label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.preferredCleaningDay || ''}
                    onChange={(e) => setEditForm({...editForm, preferredCleaningDay: e.target.value as Property['preferredCleaningDay'] || undefined})}
                  >
                    <option value="">No preference</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time Slot</label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    value={editForm.requestedTimeSlot || ''}
                    onChange={(e) => setEditForm({...editForm, requestedTimeSlot: e.target.value as Property['requestedTimeSlot'] || undefined})}
                  >
                    <option value="">No preference</option>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
      ) : (
        /* Property Details View */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Property Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="text-indigo-600">
                    {getPropertyTypeIcon(property.propertyType)}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">{property.addressLine1}</h2>
                    <div className="flex items-start space-x-2 text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mt-1" />
                      <span><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURI(formatAddress(property))}`}>{formatAddress(property)}</a></span>
                    </div>
                  </div>
                </div>
              </div>
              {customer && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Property Owner</p>
                      <p className="text-base font-medium text-gray-900">{customer.name}</p>
                    </div>
                    <Link
                      to={`/customers/${property.customerId}`}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      View Customer
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {property.squareFootage && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{NumberUtils.formatNumber(property.squareFootage, 0)}</div>
                    <div className="text-sm text-gray-500">Square Feet</div>
                  </div>
                )}
                {property.bedrooms && property.bedrooms > 0 && (
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Square className="h-4 w-4 text-gray-400" />
                      <div className="text-2xl font-bold text-gray-900">{property.bedrooms}</div>
                    </div>
                    <div className="text-sm text-gray-500">Bedrooms</div>
                  </div>
                )}
                {property.bathrooms && property.bathrooms > 0 && (
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Hash className="h-4 w-4 text-gray-400" />
                      <div className="text-2xl font-bold text-gray-900">{property.bathrooms}</div>
                    </div>
                    <div className="text-sm text-gray-500">Bathrooms</div>
                  </div>
                )}
                {property.floors && (
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Building className="h-4 w-4 text-gray-400" />
                      <div className="text-2xl font-bold text-gray-900">{property.floors}</div>
                    </div>
                    <div className="text-sm text-gray-500">Floors</div>
                  </div>
                )}
              </div>
            </div>

            {/* Access Instructions */}
            {property.accessInstructions && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Key className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Access Instructions</h3>
                </div>
                <p className="text-gray-700">{property.accessInstructions}</p>
              </div>
            )}

            {/* Special Instructions */}
            {property.specialInstructions && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Special Instructions</h3>
                <p className="text-gray-700">{property.specialInstructions}</p>
              </div>
            )}

            {/* Recent Jobs */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Jobs</h3>
              <div className="space-y-3">
                {jobs
                  .filter(job => job.property?.id === property.id)
                  .slice(0, 5)
                  .map((job) => (
                    <Link
                      key={job.id}
                      to={`/jobs/${job.id}`}
                      className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">{job.service.name}</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getJobStatusBadge(job.status)}`}>
                          {job.status.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          <span>{job.business?.businessName || 'Unassigned'}</span>
                          <span>{DateUtils.formatDateLong(job.date_time)} at {DateUtils.formatTimeFromDate(job.date_time)}</span>
                        </div>
                        <span className="font-medium">${NumberUtils.formatCurrency(job.price)}</span>
                      </div>
                    </Link>
                  ))}
                {/* Show message if no jobs */}
                {jobs.filter(job => job.property?.id === property.id).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No recent jobs for this property</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Property Owner */}
            {customer && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Owner</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{customer.name}</span>
                    <Link
                      to={`/customers/${property.customerId}`}
                      className="text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                  <Link
                    to={`/customers/${property.customerId}`}
                    className="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                  >
                    View Customer Details
                  </Link>
                </div>
              </div>
            )}

            {/* Service Preferences */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Preferences</h3>
              <div className="space-y-3">
                {property.preferredCleaningDay && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Preferred Day:</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">{property.preferredCleaningDay}</span>
                  </div>
                )}
                {property.requestedTimeSlot && (
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Preferred Time:</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">{property.requestedTimeSlot}</span>
                  </div>
                )}
                {!property.preferredCleaningDay && !property.requestedTimeSlot && (
                  <p className="text-sm text-gray-500">No specific preferences set</p>
                )}
              </div>
            </div>

            {/* Pet Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Heart className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Pet Information</h3>
              </div>
              {property.hasPets ? (
                <div>
                  <p className="text-sm text-green-600 font-medium mb-2">Has Pets</p>
                  {property.petDetails && (
                    <p className="text-sm text-gray-700">{property.petDetails}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No pets</p>
              )}
            </div>

            {/* Property Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Status</h3>
              <div className="flex items-center space-x-2">
                <div className={`h-2 w-2 rounded-full ${property.isActive ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span className={`text-sm font-medium ${property.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {property.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Property Metadata */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Property Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Property ID:</span>
                  <span className="font-medium text-gray-900">#{property.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="font-medium text-gray-900">{property.createdAt}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated:</span>
                  <span className="font-medium text-gray-900">{property.updatedAt}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyDetail;
